import { useEffect } from 'react';

/**
 * Polyfills scroll snapping by locking a scrollable container to the closest child
 * when native CSS scroll snap is not available. The hook is inert on platforms
 * that already support scroll snapping, so it is safe to use as a progressive enhancement.
 *
 * @param {import('react').RefObject<HTMLElement>} containerRef - Ref to the scroll container.
 * @param {{ snapDelay?: number }} [options] - Configuration overrides.
 * @param {number} [options.snapDelay=120] - Debounce delay before a snap is triggered.
 */
export function useScrollSnap(containerRef, { snapDelay = 120 } = {}) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    const supportsNativeSnap =
      typeof CSS !== 'undefined' &&
      (typeof CSS.supports === 'function' &&
        (CSS.supports('scroll-snap-type: y mandatory') ||
          CSS.supports('scroll-snap-type', 'y mandatory')));

    if (supportsNativeSnap) {
      return undefined;
    }

    let prefersReducedMotion = false;
    let mediaQuery;

    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      prefersReducedMotion = mediaQuery.matches;
    }

    const handleMediaChange = (event) => {
      prefersReducedMotion = event.matches;
    };

    mediaQuery?.addEventListener?.('change', handleMediaChange);
    if (!mediaQuery?.addEventListener && mediaQuery?.addListener) {
      mediaQuery.addListener(handleMediaChange);
    }

    let animationFrame = 0;
    let timeoutId = 0;

    const getCards = () =>
      Array.from(container.querySelectorAll('[data-reel-index]'));

    const snapToNearest = () => {
      animationFrame = 0;

      if (prefersReducedMotion) {
        return;
      }

      const cards = getCards();
      if (!cards.length) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const midpoint = containerRect.top + containerRect.height / 2;

      let closest = cards[0];
      let smallestDistance = Number.POSITIVE_INFINITY;

      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const cardMidpoint = rect.top + rect.height / 2;
        const distance = Math.abs(midpoint - cardMidpoint);

        if (distance < smallestDistance) {
          smallestDistance = distance;
          closest = card;
        }
      });

      if (smallestDistance <= 1) {
        return;
      }

      closest.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    };

    const scheduleSnap = () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
      animationFrame = requestAnimationFrame(snapToNearest);
    };

    const handleScroll = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      timeoutId = window.setTimeout(scheduleSnap, snapDelay);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
      if (mediaQuery?.removeEventListener) {
        mediaQuery.removeEventListener('change', handleMediaChange);
      } else if (mediaQuery?.removeListener) {
        mediaQuery.removeListener(handleMediaChange);
      }
    };
  }, [containerRef, snapDelay]);
}
